import React from 'react';
import { useTranslation } from "react-i18next";
import MiniTvUiOverlayContext from 'features/video/components/mini-tv/MiniTvUiOverlayContext';

import { isFalsy } from 'shared/utils/common';
import EVENT_NAMES from 'shared/constants/events';
import Emitter from 'shared/events/emitter';

import NextEventCountdown from 'shared/components/countdown/NextEventCountdown';
import EventBetsPanel from 'features/tickets/components/EventBetsPanel';
import MiniTvViewer from 'features/video/components/mini-tv/MiniTvViewer';
import { useServerNow } from 'shared/queries/serverTime';

import { useUserSessionContext } from 'features/session/userSessionStore';
import * as MediaEventStore from 'features/video/stores';
import { useMiniTvRun } from 'features/video/stores';
import { UPCOMING_EVENTS_LIST_SIZE, type Football3dWeek } from 'features/events/api';
import { useQueryClient } from '@tanstack/react-query';
import { useSchedulesQuery, useUpcomingEventsQuery, useScheduleUpcomingQuery, flattenScheduleUpcomingToEvents, fetchAndMergeUpcoming, eventsQueryKeys } from 'features/events/queries';
import { useScheduleContext } from 'features/events/context/ScheduleContext';
import { useEventResultsHandler } from 'features/events/hooks/useEventResultsHandler';
import { useEventResultsFetcher } from 'features/events/hooks/useEventResultsFetcher';
import { useLiveEventPin } from 'features/events/hooks/useLiveEventPin';
import { useWinChecker } from 'features/events/hooks/useWinChecker';
import { useMediaStoreSync } from 'features/video/hooks/useMediaStoreSync';
import { useScheduleChangeCleanup } from 'features/events/hooks/useScheduleChangeCleanup';
import { useMiniTvController } from 'features/video/hooks/useMiniTvController';
import { useMiniTvOrchestrator } from 'features/video/hooks/useMiniTvOrchestrator';
import type { DisplayExecutionRun } from 'features/video/hooks/useMiniTvOrchestrator';
import { miniTvCursorKey, useMiniTvCursorStore } from 'features/video/miniTvCursorStore';
import { liveUntilSecFromEventLike } from 'features/realtime/liveFromResults';
import { markExecutionEnded, clearExecutionEnded } from 'features/events/stores/executionEndedStore';
import { getEventIdForDisplay, safeEventIdForLabel } from 'features/events/eventId';
import type { Event as VirtualEvent } from 'features/events/types';

type EventRecord = Record<string, unknown>;

const RACING_SPORT_IDS = [10, 11, 20, 21, 30];
const BASEBALL_SPORT_ID = 40;
const FOOTBALL_SPORT_ID = 50;

// ---------------------------------------------------------------------------
// MiniTvCanvasWrapper
// ---------------------------------------------------------------------------

const TV_WIDTH = 1920;
const TV_HEIGHT = 1080;
const BAR_HEIGHT_RATIO = 0.105;
const BAR_HEIGHT_MIN = 36;
const BAR_HEIGHT_MAX = 78;
const GAME_BOX_BASE_MIN = 32;
const GAME_BOX_BASE_MAX = 72;
const GAME_BOX_BASE_RATIO = 0.09;

interface MiniTvCanvasWrapperProps {
  children: React.ReactNode;
  overlayChildren?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  fullscreenRef: React.RefObject<HTMLDivElement | null> | ((node: HTMLDivElement | null) => void);
  baseballBarAnchorRef: React.RefObject<HTMLDivElement | null> | null;
}

function MiniTvCanvasWrapper({ children, overlayChildren, className, style, fullscreenRef, baseballBarAnchorRef }: MiniTvCanvasWrapperProps) {
  const outerRef = React.useRef<HTMLDivElement | null>(null);
  const uiOverlayRef = React.useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = React.useState(1);
  const [barHeight, setBarHeight] = React.useState<number | null>(null);
  const [gameBoxBase, setGameBoxBase] = React.useState<number | null>(null);

  React.useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry?.contentRect) return;
      const { width, height } = entry.contentRect;
      if (typeof width !== 'number' || typeof height !== 'number' || width <= 0 || height <= 0) return;
      const s = Math.min(width / TV_WIDTH, height / TV_HEIGHT);
      setScale(s);
      if (baseballBarAnchorRef && typeof height === 'number' && height > 0) {
        const h = Math.round(Math.max(BAR_HEIGHT_MIN, Math.min(BAR_HEIGHT_MAX, height * BAR_HEIGHT_RATIO)));
        setBarHeight(h);
        const gb = Math.round(Math.max(GAME_BOX_BASE_MIN, Math.min(GAME_BOX_BASE_MAX, height * GAME_BOX_BASE_RATIO)));
        setGameBoxBase(gb);
      }
    });
    ro.observe(el);
    const { width, height } = el.getBoundingClientRect();
    if (width > 0 && height > 0) {
      setScale(Math.min(width / TV_WIDTH, height / TV_HEIGHT));
      if (baseballBarAnchorRef && height > 0) {
        const h = Math.round(Math.max(BAR_HEIGHT_MIN, Math.min(BAR_HEIGHT_MAX, height * BAR_HEIGHT_RATIO)));
        setBarHeight(h);
        const gb = Math.round(Math.max(GAME_BOX_BASE_MIN, Math.min(GAME_BOX_BASE_MAX, height * GAME_BOX_BASE_RATIO)));
        setGameBoxBase(gb);
      }
    }
    return () => ro.disconnect();
  }, [baseballBarAnchorRef]);

  const safeScale = Math.max(scale, 0.15);
  const overlayScale = safeScale >= 1 ? 1 : Math.min(1.6, 1 / safeScale);
  const contentScale = safeScale >= 1 ? 1 : Math.min(4, 1 / safeScale);

  const wrapperStyle: Record<string, unknown> = {
    ...style,
    '--c7-mini-tv-overlay-scale': overlayScale,
    '--c7-mini-tv-content-scale': contentScale,
    position: 'relative' as const,
  };
  if (barHeight != null) {
    wrapperStyle['--c7-mini-tv-bar-height'] = `${barHeight}px`;
  }
  if (gameBoxBase != null) {
    wrapperStyle['--c7-mini-tv-baseball-game-box-base'] = `${gameBoxBase}px`;
  }

  return (
    <MiniTvUiOverlayContext.Provider value={uiOverlayRef}>
      <div
        ref={(node) => {
          outerRef.current = node;
          if (typeof fullscreenRef === 'function') fullscreenRef(node);
          else if (fullscreenRef) (fullscreenRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }}
        className={className}
        style={wrapperStyle as React.CSSProperties}
      >
        {baseballBarAnchorRef && (
          <div
            ref={baseballBarAnchorRef as React.RefObject<HTMLDivElement>}
            className="c7-mini-tv-baseball-bar-anchor"
            aria-hidden="true"
          />
        )}
        <div style={{ position: 'relative', width: TV_WIDTH * scale, height: TV_HEIGHT * scale }}>
          <div
            style={{
              width: '100%',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: TV_WIDTH,
                height: TV_HEIGHT,
                position: 'relative',
                overflow: 'hidden',
                transform: `scale(${scale})`,
                transformOrigin: '0 0',
              }}
            >
              {children}
            </div>
          </div>
          <div
            ref={uiOverlayRef}
            className="c7-mini-tv-ui-overlay-layer"
          >
            {overlayChildren}
          </div>
        </div>
      </div>
    </MiniTvUiOverlayContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// EventViewer
// ---------------------------------------------------------------------------

interface EventViewerProps {
  inline_key: string;
  layoutContext: 'desktop' | 'mobile';
  theaterMode?: boolean;
  onTheaterModeChange?: (value: boolean) => void;
  accordionExpanded?: boolean;
  onAccordionExpandedChange?: (value: boolean) => void;
  eventIdForBetsPanel?: number | string | null;
  isActiveLayout?: boolean;
}

function EventViewer(props: EventViewerProps) {
  const { t } = useTranslation();
  const {
    inline_key,
    layoutContext,
    theaterMode = false,
    onTheaterModeChange,
    accordionExpanded: controlledAccordionExpanded,
    onAccordionExpandedChange,
    eventIdForBetsPanel,
    isActiveLayout = true,
  } = props;
  const [current_result, setCurrentResult] = React.useState<Record<string, unknown>>({});
  const [isMuted, setIsMuted] = React.useState(true);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [isTabVisible, setIsTabVisible] = React.useState(
    typeof document !== 'undefined' ? !document.hidden : true
  );
  const [internalAccordionExpanded, setInternalAccordionExpanded] = React.useState(false);
  const isAccordionControlled = controlledAccordionExpanded !== undefined && typeof onAccordionExpandedChange === 'function';
  const tvAccordionExpanded = isAccordionControlled ? controlledAccordionExpanded : internalAccordionExpanded;
  const tvAccordionExpandedRef = React.useRef(tvAccordionExpanded);
  tvAccordionExpandedRef.current = tvAccordionExpanded;
  const setTvAccordionExpanded = React.useCallback(
    (valueOrUpdater: boolean | ((prev: boolean) => boolean)) => {
      const next =
        typeof valueOrUpdater === 'function'
          ? valueOrUpdater(tvAccordionExpandedRef.current)
          : valueOrUpdater;
      if (isAccordionControlled) {
        onAccordionExpandedChange?.(next);
      } else {
        setInternalAccordionExpanded(next);
      }
    },
    [isAccordionControlled, onAccordionExpandedChange]
  );
  React.useEffect(() => {
    const handleVisibilityChange = () => setIsTabVisible(!document.hidden);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);
  const queryClient = useQueryClient();
  const [{ user }, userDispatch] = useUserSessionContext();
  const [{
    event_results: from_media_results,
    status_group: { HAS_ENDED, VIDEO_IS_PLAYING }
  }, mediaDispatch] = MediaEventStore.useMediaEventContext();
  const scheduleCtx = useScheduleContext();
  const current_schedule_id = scheduleCtx.currentScheduleId;
  const selectedEvent = scheduleCtx.selectedEvent;
  const selectedWeekId = scheduleCtx.selectedWeekId;
  const eventStartedFromContext = scheduleCtx.eventStarted;
  const inLiveWindowByTime = scheduleCtx.inLiveWindowByTime;
  const nextEventIdFromContext = scheduleCtx.eventId;
  const eventTimestampFromContext = scheduleCtx.eventTimestamp;
  const endTimestampFromContext = scheduleCtx.endTimestamp;
  const isLiveFromContext = scheduleCtx.isLive;
  const countdownSecondsFromContext = scheduleCtx.countdownSeconds;
  const ongoingPayloadFromContext = scheduleCtx.ongoingPayload;
  const refetchOngoing = scheduleCtx.refetchOngoing;
  const { data: schedules = [] } = useSchedulesQuery();

  const { odds_format, language } = user || {};
  const { event_id, has_ongoing = false, error = false, error_message } = (current_result || {}) as EventRecord;
  const resultsHandler = useEventResultsHandler(current_schedule_id, event_id as string | number | undefined, {
    results: current_result ?? {},
    isDelayed: false,
  });

  const [stableNextEvent, setStableNextEvent] = React.useState<Record<string, unknown> | null>(null);

  const current_schedule = schedules?.find((s: EventRecord) => s.schedule_id === current_schedule_id) as EventRecord | undefined;
  const sportObj = current_schedule?.sport as EventRecord | undefined;
  const scheduleSportId = sportObj?.sport_id ?? current_schedule?.sport_id ?? null;
  const isRacingSport =
    (sportObj?.sport_id != null && RACING_SPORT_IDS.includes(Number(sportObj.sport_id))) ||
    sportObj?.is_racing === true ||
    sportObj?.is_racing === 1 ||
    parseInt(String(sportObj?.is_racing ?? 0)) === 1;
  const isRacingSchedule = isRacingSport;
  const isBaseballSchedule =
    Number(sportObj?.sport_id ?? current_schedule?.sport_id) === BASEBALL_SPORT_ID;
  const isFootball3dSchedule =
    Number(sportObj?.sport_id ?? current_schedule?.sport_id) === FOOTBALL_SPORT_ID;

  const { data: current_events_http = [] } = useUpcomingEventsQuery({
    scheduleId: current_schedule_id,
    numEvents: UPCOMING_EVENTS_LIST_SIZE,
  });

  const serverNow = useServerNow();
  const nowSec = Math.floor(serverNow / 1000);
  const nowSecRef = React.useRef<number>(nowSec);
  nowSecRef.current = nowSec;

  const { data: scheduleUpcoming } = useScheduleUpcomingQuery({
    scheduleId: current_schedule_id ?? undefined,
    numEvents: UPCOMING_EVENTS_LIST_SIZE,
  });
  const ongoingPayload = ongoingPayloadFromContext;
  const selectedWeekNumber = React.useMemo((): number | null => {
    if (selectedWeekId == null || !scheduleUpcoming || typeof scheduleUpcoming !== 'object' || !('weeks' in scheduleUpcoming)) return null;
    return ((scheduleUpcoming as { weeks?: Football3dWeek[] }).weeks ?? []).find(w => w.week_id === selectedWeekId)?.week_number ?? null;
  }, [selectedWeekId, scheduleUpcoming]);
  const upcomingWithOngoing = React.useMemo((): EventRecord[] | null => {
    if (isFootball3dSchedule && current_schedule_id != null) return null;
    if (!scheduleUpcoming || typeof scheduleUpcoming !== 'object') return null;
    const flat = flattenScheduleUpcomingToEvents(scheduleUpcoming);
    const list: EventRecord[] = Array.isArray(flat) ? [...flat] as EventRecord[] : [];
    if (ongoingPayload?.event_id != null && current_schedule_id != null) {
      const ongoingId = String(ongoingPayload.event_id);
      if (!list.some((e) => getEventIdForDisplay(e) === ongoingId)) {
        const ts = ongoingPayload.event_timestamp;
        if (ts == null || !Number.isFinite(Number(ts))) return list;
        const liveTs = Number(ts);
        const asEvent: EventRecord = { ...ongoingPayload, schedule_id: current_schedule_id };
        const idx = list.findIndex((e) => e?.event_timestamp != null && Number(e.event_timestamp) > liveTs);
        if (idx < 0) return [...list, asEvent];
        return [...list.slice(0, idx), asEvent, ...list.slice(idx)];
      }
    }
    return list;
  }, [scheduleUpcoming, current_schedule_id, isFootball3dSchedule, ongoingPayload]);
  const footballEventsForWeek = React.useMemo((): EventRecord[] => {
    if (!isFootball3dSchedule || !scheduleUpcoming || typeof scheduleUpcoming !== 'object' || !('weeks' in scheduleUpcoming)) return [];
    const weeks = (scheduleUpcoming as { weeks?: Football3dWeek[] }).weeks ?? [];
    const weekObj = selectedWeekId != null ? weeks.find(w => w.week_id === selectedWeekId) : null;
    const weekList: EventRecord[] = weekObj?.events ? [...weekObj.events] as EventRecord[] : [];
    if (ongoingPayload?.event_id != null && ongoingPayload?.week_id != null && Number(ongoingPayload.week_id) === Number(selectedWeekId) && current_schedule_id != null) {
      const ongoingId = String(ongoingPayload.event_id);
      if (!weekList.some((e) => getEventIdForDisplay(e) === ongoingId)) {
        const ts = ongoingPayload.event_timestamp;
        if (ts == null || !Number.isFinite(Number(ts))) return weekList;
        const liveTs = Number(ts);
        const asEvent: EventRecord = { ...ongoingPayload, schedule_id: current_schedule_id };
        const idx = weekList.findIndex((e) => e?.event_timestamp != null && Number(e.event_timestamp) > liveTs);
        if (idx < 0) return [...weekList, asEvent];
        return [...weekList.slice(0, idx), asEvent, ...weekList.slice(idx)];
      }
    }
    return weekList;
  }, [isFootball3dSchedule, scheduleUpcoming, selectedWeekId, ongoingPayload, current_schedule_id]);

  const current_events: EventRecord[] =
    isFootball3dSchedule && current_schedule_id != null
      ? footballEventsForWeek
      : (Array.isArray(upcomingWithOngoing)
          ? upcomingWithOngoing
          : (Array.isArray(current_events_http) ? current_events_http as EventRecord[] : []));

  const orderedUpcomingEvents = React.useMemo((): EventRecord[] => {
    const list = Array.isArray(current_events) ? current_events : [];
    return list
      .slice()
      .filter((e) => e && e.event_id != null && e.event_timestamp != null)
      .sort((a, b) => Number(a.event_timestamp) - Number(b.event_timestamp));
  }, [current_events]);

  const miniTvCursorStoreKey = React.useMemo(() => {
    return miniTvCursorKey({
      scheduleId: current_schedule_id,
      week: isFootball3dSchedule ? selectedWeekId : null,
    });
  }, [current_schedule_id, isFootball3dSchedule, selectedWeekId]);

  const cursorEventId = useMiniTvCursorStore((s: Record<string, unknown>) => (s.cursorEventIdByKey as Record<string, unknown>)?.[miniTvCursorStoreKey] ?? null);
  const setCursorEventId = useMiniTvCursorStore((s: Record<string, unknown>) => s.setCursorEventId as (key: string, id: string | number) => void);

  const resolvedNextEvent = React.useMemo((): EventRecord | null => {
    const list = orderedUpcomingEvents;
    if (!list.length) return null;

    const filteredList = list.filter((e) => {
      const liveUntil = liveUntilSecFromEventLike(e);
      return liveUntil == null || !Number.isFinite(liveUntil) || nowSec < liveUntil;
    });

    const isStartedOrLive = (e: EventRecord) => {
      const start = e?.event_timestamp != null ? Number(e.event_timestamp) : null;
      if (start == null || !Number.isFinite(start) || nowSec < start) return false;
      const liveUntil = liveUntilSecFromEventLike(e);
      return liveUntil == null || !Number.isFinite(liveUntil) || nowSec < liveUntil;
    };

    if (isFootball3dSchedule) {
      const startedEvents = filteredList.filter(isStartedOrLive);
      if (startedEvents.length > 0) {
        const featured = startedEvents.find(
          (e) => e?.featured === true || e?.featured === 1 || String(e?.featured ?? '') === 'true'
        );
        const chosen = featured ?? startedEvents[0];
        return selectedWeekId != null ? { ...chosen, week_id: selectedWeekId } : chosen;
      }
    }

    const firstCurrentlyLive = filteredList.find((e) => {
      const start = e?.event_timestamp != null ? Number(e.event_timestamp) : null;
      const liveUntil = liveUntilSecFromEventLike(e);
      return (
        start != null &&
        Number.isFinite(start) &&
        liveUntil != null &&
        Number.isFinite(liveUntil) &&
        nowSec >= start &&
        nowSec < liveUntil
      );
    });
    if (firstCurrentlyLive) return firstCurrentlyLive;

    const firstStartedButNotEnded = filteredList.find((e) => {
      const start = e?.event_timestamp != null ? Number(e.event_timestamp) : null;
      const liveUntil = liveUntilSecFromEventLike(e);
      if (start == null || !Number.isFinite(start) || nowSec < start) return false;
      if (liveUntil == null || !Number.isFinite(liveUntil)) return true;
      return nowSec < liveUntil;
    });
    if (firstStartedButNotEnded) return firstStartedButNotEnded;

    const cursorId = cursorEventId != null ? String(cursorEventId) : null;
    if (cursorId) {
      const match = filteredList.find((e) => String(e?.event_id) === cursorId);
      if (match) {
        const liveUntil = liveUntilSecFromEventLike(match);
        if (liveUntil == null || nowSec < liveUntil) return match;
      }
    }

    const firstNotEnded = filteredList.find((e) => {
      const liveUntil = liveUntilSecFromEventLike(e);
      if (liveUntil != null && Number.isFinite(liveUntil)) return nowSec < liveUntil;
      const ts = e?.event_timestamp != null ? Number(e.event_timestamp) : null;
      return ts != null && Number.isFinite(ts) && ts >= nowSec;
    });
    const firstFuture = filteredList.find((e) => {
      const ts = e?.event_timestamp != null ? Number(e.event_timestamp) : null;
      return ts != null && Number.isFinite(ts) && ts >= nowSec;
    });
    return firstNotEnded ?? firstFuture ?? filteredList[0] ?? null;
  }, [orderedUpcomingEvents, cursorEventId, isFootball3dSchedule, nowSec, selectedWeekId]);

  React.useEffect(() => {
    const list = orderedUpcomingEvents;
    if (!list.length) return;
    const resolvedId = resolvedNextEvent?.event_id != null ? String(resolvedNextEvent.event_id) : null;
    if (!resolvedId) return;
    const cursorId = cursorEventId != null ? String(cursorEventId) : null;
    const cursorMatch = cursorId ? list.find((e) => String(e?.event_id) === cursorId) : null;
    const cursorLiveUntil = liveUntilSecFromEventLike(cursorMatch);
    const cursorEventEnded =
      cursorLiveUntil != null && Number.isFinite(cursorLiveUntil) && nowSec >= cursorLiveUntil;
    const cursorStillValid = cursorMatch && !cursorEventEnded;
    if (!cursorStillValid && resolvedId !== cursorId) {
      setCursorEventId(miniTvCursorStoreKey, resolvedId);
    }
  }, [orderedUpcomingEvents, resolvedNextEvent?.event_id, cursorEventId, miniTvCursorStoreKey, setCursorEventId, nowSec]);

  const sport_id: number | string | null =
    (sportObj?.sport_id as number | string | null) ||
    ((isRacingSchedule || isBaseballSchedule) ? (current_events?.[0]?.sport_id as number | string | null ?? null) : ((current_events_http as EventRecord[])?.[0]?.sport_id as number | string | null ?? null));

  const candidate_next_event = resolvedNextEvent;

  const eventStarted = eventStartedFromContext;
  const ongoingRec = ongoingPayload as EventRecord | null;
  const hasOngoingCurrent =
    ongoingRec != null &&
    ongoingRec.event_id != null &&
    String(ongoingRec.event_id).trim() !== '';
  const nextEventWeekNumber = React.useMemo((): number | null => {
    if (!isFootball3dSchedule) return null;
    const ne = scheduleCtx.scheduleStatus?.nextEvent;
    if (!ne || ne.week_number == null) return null;
    return Number(ne.week_number);
  }, [isFootball3dSchedule, scheduleCtx.scheduleStatus?.nextEvent]);
  const countdownData = {
    eventId: nextEventIdFromContext,
    eventTimestamp: eventTimestampFromContext,
    endTimestamp: endTimestampFromContext,
    isLive: isLiveFromContext,
    isFootball3d: isFootball3dSchedule,
    week: isFootball3dSchedule ? nextEventWeekNumber : null,
  };
  const schedulePhase = scheduleCtx.scheduleStatus?.phase ?? 'idle';
  const isLivePhase = schedulePhase === 'live';
  const isStartingPhase = schedulePhase === 'starting';
  const accordionLabelLive = isLivePhase || isStartingPhase;

  React.useEffect(() => {
    if (!isBaseballSchedule && !isFootball3dSchedule) {
      setStableNextEvent(null);
      return;
    }

    setStableNextEvent((prev): EventRecord | null => {
      const candidate = candidate_next_event;
      const curId = cursorEventId != null ? String(cursorEventId) : null;
      const candId = candidate?.event_id != null ? String(candidate.event_id) : null;
      const prevId = prev?.event_id != null ? String(prev.event_id) : null;

      const prevStart = prev?.event_timestamp != null ? Number(prev.event_timestamp) : null;
      const prevHasStart = prevStart != null && Number.isFinite(prevStart) && prevStart > 0;
      const prevLiveUntil = liveUntilSecFromEventLike(prev);
      const prevInLiveWindow = prevHasStart && prevLiveUntil != null && nowSecRef.current >= prevStart && nowSecRef.current < prevLiveUntil;

      if (curId && candId && candId === curId && prevId && prevId !== candId) {
        return candidate;
      }

      if (!candidate?.event_id || candidate?.event_timestamp == null) {
        if (prevInLiveWindow) return prev;
        return candidate ?? null;
      }

      if (!prev?.event_id || !prevHasStart) return candidate;

      const candStart = Number(candidate.event_timestamp);
      const candHasStart = Number.isFinite(candStart) && candStart > 0;

      if (prevInLiveWindow) return prev;
      if (nowSecRef.current < prevStart! && candHasStart && candStart > prevStart!) return prev;

      return candidate;
    });
  }, [isBaseballSchedule, isFootball3dSchedule, candidate_next_event?.event_id, candidate_next_event?.event_timestamp, nowSec, cursorEventId]);

  const next_event: EventRecord | null =
    hasOngoingCurrent && ongoingPayload
      ? ongoingPayload as EventRecord
      : (isBaseballSchedule || isFootball3dSchedule)
        ? (stableNextEvent ?? candidate_next_event)
        : candidate_next_event;

  const next_event_id = next_event?.event_id as string | number | undefined;
  const next_event_timestamp = next_event?.event_timestamp;
  const next_event_end_timestamp =
    next_event?.end_timestamp != null && Number.isFinite(Number(next_event.end_timestamp))
      ? Number(next_event.end_timestamp)
      : (next_event_timestamp != null &&
          next_event?.duration != null &&
          Number.isFinite(Number(next_event_timestamp)) &&
          Number.isFinite(Number(next_event.duration))
          ? Number(next_event_timestamp) + Number(next_event.duration)
          : null);

  const { getRun, getActiveExecutionRuns, startRun, setRunEnded, clearRun } = useMiniTvRun();
  const run = getRun(current_schedule_id!);
  const activeExecutionRuns = getActiveExecutionRuns();

  const livePin = useLiveEventPin(eventStarted, next_event_id);
  const { liveEventId, liveEventIdRef: liveEventIdPinnedRef } = livePin;

  const showNextEventBar =
    current_schedule_id != null &&
    (countdownData.eventId != null || eventStarted || (run != null && run.phase !== 'ended'));

  const advanceCursorAfterExecution = React.useCallback(
    (params: { eventId?: string | number | null; eventTimestamp?: number | null; week?: number | null }) => {
      const completedEventId = params?.eventId != null ? String(params.eventId) : null;
      const completedTs = params?.eventTimestamp != null ? Number(params.eventTimestamp) : null;
      const completedWeek = params?.week != null ? params.week : null;

      const list = orderedUpcomingEvents;
      if (!list.length) return;

      const notEnded = (e: EventRecord) => {
        const liveUntil = liveUntilSecFromEventLike(e);
        return liveUntil == null || nowSec < liveUntil;
      };

      let next: EventRecord | null = null;
      if (completedEventId) {
        const idx = list.findIndex((e) => String(e?.event_id) === completedEventId);
        if (idx >= 0) {
          const candidate = list[idx + 1] ?? null;
          if (candidate && notEnded(candidate)) next = candidate;
        }
      }

      if (!next) {
        if (completedTs != null && Number.isFinite(completedTs)) {
          next = list.find((e) => e?.event_timestamp != null && Number(e.event_timestamp) > completedTs && notEnded(e)) ?? null;
        }
        if (!next) {
          next = list.find((e) => e?.event_timestamp != null && Number(e.event_timestamp) >= nowSec && notEnded(e)) ?? null;
        }
      }

      if (!next?.event_id) return;
      setCursorEventId(
        miniTvCursorKey({
          scheduleId: current_schedule_id,
          week: isFootball3dSchedule ? (completedWeek ?? selectedWeekId) : null,
        }),
        next.event_id as string | number
      );
    },
    [orderedUpcomingEvents, nowSec, current_schedule_id, isFootball3dSchedule, selectedWeekId, setCursorEventId]
  );

  const maybeRefetchUpcoming = React.useCallback(() => {
    if (!current_schedule_id) return;
    const now = Date.now() / 1000;

    if (isFootball3dSchedule) {
      const cached = queryClient.getQueryData(
        eventsQueryKeys.scheduleUpcoming(current_schedule_id)
      ) as { weeks?: Football3dWeek[] } | undefined;
      const weeks = cached?.weeks ?? [];
      const futureWeeks = weeks.filter((w) => {
        const events = Array.isArray(w.events) ? w.events : [];
        const timestamps = events.map((e: any) => Number(e.event_timestamp)).filter(Number.isFinite);
        const kickoff = timestamps.length > 0 ? Math.min(...timestamps) : 0;
        return kickoff > now;
      });
      if (futureWeeks.length >= 5) return;
    } else {
      const futureEvents = orderedUpcomingEvents.filter(
        (e) => Number(e.event_timestamp) > now
      );
      if (futureEvents.length >= 5) return;
    }

    void fetchAndMergeUpcoming(queryClient, current_schedule_id, isFootball3dSchedule);
  }, [current_schedule_id, isFootball3dSchedule, orderedUpcomingEvents, queryClient]);

  const miniTvResetRef = React.useRef<(() => void) | null>(null);
  const fullscreenTargetRef = React.useRef<HTMLDivElement | null>(null);
  const baseballBarAnchorRef = React.useRef<HTMLDivElement | null>(null);
  const presentationEventIdRef = React.useRef<string | number | null>(null);

  const hardStopMiniTvVideos = React.useCallback(() => {
    if (typeof document === 'undefined') return;
    const root = fullscreenTargetRef.current ?? document;
    const videos = root.querySelectorAll('video[id^="c7-mini-tv-"]');
    videos.forEach((v) => {
      try {
        (v as HTMLVideoElement).pause?.();
        v.removeAttribute?.('src');
        (v as HTMLVideoElement).load?.();
      } catch {
        // ignore
      }
    });
  }, []);

  // orchestratorResetRef is set after useMiniTvOrchestrator below
  const orchestratorResetRef = React.useRef<(() => void) | null>(null);

  const scheduleCleanupCallback = React.useCallback((_prevScheduleId: string | number | null) => {
    presentationEventIdRef.current = null;
    orchestratorResetRef.current?.();
    setStableNextEvent(null);
    miniTvResetRef.current?.();
  }, []);

  const { scheduleJustChanged } = useScheduleChangeCleanup({
    currentScheduleId: current_schedule_id,
    clearRun,
    hardStopMiniTvVideos,
    mediaDispatch,
    setTvAccordionExpanded,
    resetLivePin: livePin.resetPin,
    onCleanup: scheduleCleanupCallback,
  });

  const miniTvCtxKey = `${current_schedule_id ?? ''}:${sport_id ?? ''}:${getEventIdForDisplay({ event_id: liveEventId ?? next_event_id })}`;
  const miniTvCtxKeyRef = React.useRef(miniTvCtxKey);
  miniTvCtxKeyRef.current = miniTvCtxKey;

  if (!run && eventStarted && tvAccordionExpanded) {
    if (presentationEventIdRef.current == null) {
      presentationEventIdRef.current = liveEventId ?? next_event_id ?? null;
    }
  } else {
    presentationEventIdRef.current = null;
  }
  const stablePresentationKeyEventId = presentationEventIdRef.current ?? liveEventId ?? next_event_id;

  React.useEffect(() => {
    if (
      run?.phase === 'ended' &&
      next_event_id != null &&
      Number(next_event_id) !== Number(run.eventId)
    ) {
      clearRun(current_schedule_id!);
    }
  }, [run?.phase, run?.eventId, eventStarted, next_event_id, current_schedule_id, clearRun]);

  const runEventId = run?.eventId as string | number | null | undefined;
  const prevRunEventIdRef = React.useRef<string | number | null | undefined>(runEventId);
  React.useEffect(() => {
    const prev = prevRunEventIdRef.current;
    prevRunEventIdRef.current = runEventId;
    if (prev != null && prev !== runEventId) {
      clearExecutionEnded(prev);
    }
  }, [runEventId]);

  // Clean-slate recovery: when tab becomes visible after being hidden,
  // clear the stale run and reset the orchestrator so fresh data drives
  // the next state transition.
  const prevTabVisibleRef = React.useRef(isTabVisible);
  React.useEffect(() => {
    const wasHidden = !prevTabVisibleRef.current;
    prevTabVisibleRef.current = isTabVisible;
    if (!isTabVisible || !wasHidden) return;
    if (current_schedule_id != null) {
      clearRun(current_schedule_id);
    }
    orchestratorResetRef.current?.();
  }, [isTabVisible, current_schedule_id, clearRun]);

  const countdownFinished = countdownSecondsFromContext == null || countdownSecondsFromContext === 0;
  const resultsFetcher = useEventResultsFetcher({
    scheduleId: current_schedule_id,
    liveEventId,
    scheduleJustChanged,
    inLiveWindowByTime,
    countdownFinished,
    sportId: sport_id,
    hasOngoingCurrent,
    ongoingPayload,
  });
  const {
    effectiveEventResults,
    isCancelled,
    isSegmentBasedSport,
    miniTvVideoId,
    videoIdSequence,
    useBatchVod,
    pollExhausted,
  } = resultsFetcher;

  useMediaStoreSync({
    scheduleId: current_schedule_id,
    eventStarted,
    ongoingPayload: ongoingPayloadFromContext,
    liveEventId,
    run,
    mediaDispatch,
  });

  const miniTv = useMiniTvController({
    scheduleId: current_schedule_id,
    eventId: liveEventId ?? next_event_id,
    sportId: sport_id != null ? Number(sport_id) : null,
    ctxKey: miniTvCtxKey,
    isRacingSchedule,
    isBaseballSchedule,
    useBatchVod,
    videoIdSequence,
    miniTvVideoId,
    effectiveEventResults,
    refetchOngoing,
  });
  miniTvResetRef.current = miniTv.reset;

  const avhFromController = scheduleJustChanged ? null : miniTv.avh;
  const stableAvhRef = React.useRef(avhFromController);
  const avhString = avhFromController != null ? String(avhFromController) : '';
  const prevAvhString = stableAvhRef.current != null ? String(stableAvhRef.current) : '';
  if (avhString !== prevAvhString) {
    stableAvhRef.current = avhFromController;
  }
  const stableAvh = stableAvhRef.current;

  // Football3D sidebar
  const footballWeek =
    (effectiveEventResults as EventRecord | undefined)?.week_id ??
    next_event?.week_id ??
    current_events?.find((e) => Number(e.event_id) === Number(liveEventId))?.week_id;
  const { otherWeekEvents: footballOtherWeekEvents, weekResults: footballWeekResults } = React.useMemo(() => {
    if (Number(sport_id) !== FOOTBALL_SPORT_ID) {
      return { otherWeekEvents: [] as EventRecord[], weekResults: {} as Record<string, EventRecord> };
    }

    const featuredId = liveEventId ?? next_event_id;
    const sidebarEventsRaw = (effectiveEventResults as EventRecord | undefined)?.sidebar_events;
    const sidebarEvents = Array.isArray(sidebarEventsRaw) ? (sidebarEventsRaw as EventRecord[]) : [];
    if (sidebarEvents.length > 0) {
      const other = sidebarEvents.filter((e) => getEventIdForDisplay(e) !== getEventIdForDisplay({ event_id: featuredId }));
      const byEvent: Record<string, EventRecord> = {};
      for (const e of other) {
        const id = getEventIdForDisplay(e);
        if (!id) continue;
        byEvent[id] = e;
      }
      return { otherWeekEvents: other, weekResults: byEvent };
    }

    const fallbackEvents = (Array.isArray(current_events) ? current_events : [])
      .filter((e) => footballWeek == null || Number(e?.week_id ?? footballWeek) === Number(footballWeek))
      .filter((e) => getEventIdForDisplay(e) !== getEventIdForDisplay({ event_id: featuredId })) as EventRecord[];
    return { otherWeekEvents: fallbackEvents, weekResults: {} as Record<string, EventRecord> };
  }, [sport_id, effectiveEventResults, current_events, liveEventId, next_event_id, footballWeek]);

  // ---- Mini TV Orchestrator ----

  const orchestrator = useMiniTvOrchestrator({
    scheduleId: current_schedule_id,
    sportId: sport_id,
    eventStarted,
    run,
    activeExecutionRuns,
    miniTv,
    miniTvCtxKey,
    miniTvCtxKeyRef,
    liveEventId,
    nextEventId: next_event_id ?? null,
    nextEvent: next_event as Record<string, unknown> | null,
    tvAccordionExpanded,
    setTvAccordionExpanded,
    isActiveLayout,
    isTabVisible,
    isCancelled,
    isSegmentBasedSport,
    useBatchVod,
    effectiveEventResults: effectiveEventResults as Record<string, unknown>,
    hasOngoingCurrent,
    ongoingPayload: ongoingPayload as Record<string, unknown> | null,
    nowSecRef,
    startRun,
    advanceCursorAfterExecution,
    maybeRefetchUpcoming,
    footballOtherWeekEvents,
    footballWeekResults,
    pollExhausted,
  });
  orchestratorResetRef.current = orchestrator.resetOrchestrator;

  const {
    displayExecutionRuns,
    shouldShowCancelledOverlay,
    shouldShowVideoErrorOverlay,
    shouldShowResultsFetchError,
    hasVideoUrls,
    joinPositionForPresentation,
    hasValidJoinPositionData,
  } = orchestrator;

  React.useEffect(() => {
    const targetRef = fullscreenTargetRef;
    const getFullscreenElement = (): Element | null =>
      (document as unknown as Record<string, Element | null>).fullscreenElement
      ?? (document as unknown as Record<string, Element | null>).webkitFullscreenElement
      ?? (document as unknown as Record<string, Element | null>).mozFullScreenElement
      ?? (document as unknown as Record<string, Element | null>).msFullscreenElement
      ?? null;
    const syncFullscreenState = () => {
      const el = getFullscreenElement();
      setIsFullscreen(el != null && el === targetRef.current);
    };
    const events = ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange'];
    events.forEach((name) => document.addEventListener(name, syncFullscreenState));
    syncFullscreenState();
    return () => {
      events.forEach((name) => document.removeEventListener(name, syncFullscreenState));
    };
  }, []);

  const requestFullscreen = React.useCallback(() => {
    const node = fullscreenTargetRef.current;
    if (!node) return;
    const fn =
      (node as unknown as Record<string, unknown>).requestFullscreen ||
      (node as unknown as Record<string, unknown>).webkitRequestFullscreen ||
      (node as unknown as Record<string, unknown>).mozRequestFullScreen ||
      (node as unknown as Record<string, unknown>).msRequestFullscreen;
    if (typeof fn === 'function') {
      try {
        fn.call(node);
      } catch {
        // ignore
      }
    }
  }, []);

  const exitFullscreen = React.useCallback(() => {
    if (typeof document === 'undefined') return;
    const doc = document as unknown as Record<string, unknown>;
    const el = doc.fullscreenElement ?? doc.webkitFullscreenElement ?? doc.mozFullScreenElement ?? doc.msFullscreenElement;
    if (!el) return;
    const fn = doc.exitFullscreen ?? doc.webkitExitFullscreen ?? doc.mozCancelFullScreen ?? doc.msExitFullscreen;
    if (typeof fn === 'function') {
      try {
        (fn as () => void).call(document);
      } catch {
        // ignore
      }
    }
  }, []);

  const handleMiniTvVideoReady = React.useCallback(() => {
    miniTv.setVideoReady(true);
  }, [miniTv]);

  const noopVideoReady = React.useCallback(() => {}, []);

  const handleRunEnded = React.useCallback(() => {
    if (run?.scheduleId != null) setRunEnded(run.scheduleId as string);
  }, [run?.scheduleId, setRunEnded]);

  useWinChecker({
    eventId: event_id as string | number | null | undefined,
    user,
    userDispatch,
    run,
    resultsHandler: resultsHandler as { checkWins: (params: { event_id: string | number }) => void; checksLoading: boolean; winResults: { new_balance?: number } | null },
    mediaHasEnded: Boolean(HAS_ENDED),
    mediaResults: (from_media_results ?? {}) as Record<string, unknown>,
    setCurrentResult,
  });

  const shouldHide = VIDEO_IS_PLAYING || isFalsy(event_id as string | number | null | undefined) || has_ongoing;

  React.useEffect(() => {
    if (!shouldHide) {
      Emitter.emit(EVENT_NAMES.FADER.CHANGE_PROCEED, inline_key);
    }
  }, [shouldHide]);

  const accordionHasContent = (run != null && run.phase !== 'ended') || (eventStarted && tvAccordionExpanded);
  const showAccordionExpandable = accordionLabelLive || (run != null && run.phase !== 'ended');
  const resultsMatchSelection =
    !selectedEvent ||
    (selectedEvent.schedule_id === current_schedule_id && Number(selectedEvent.event_id) === Number(event_id));
  const showResults =
    !error &&
    ((resultsMatchSelection && !shouldHide) || (run?.phase === 'ended' && run?.eventId)) &&
    (!showNextEventBar || !accordionHasContent);
  const showError = Boolean(error);
  const showCountdown = showNextEventBar && !accordionHasContent && !showResults && !showError;

  const handleMuteToggle = React.useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  const toggleFullscreen = React.useCallback(() => {
    if (isFullscreen) {
      exitFullscreen();
    } else {
      requestFullscreen();
    }
  }, [isFullscreen, exitFullscreen, requestFullscreen]);

  const tvControlBtn =
    'min-w-[36px] min-h-[36px] p-2 rounded-lg bg-black/45 text-white border border-white/10 backdrop-blur hover:bg-black/60 transition-colors flex items-center justify-center';
  const tvControlIcon = 'text-base md:text-lg';

  const renderTvControls = () => (
    <div className="absolute bottom-1.5 right-1.5 md:bottom-2 md:right-2 z-20 flex items-center gap-1.5 md:gap-2">
      <button
        type="button"
        className={tvControlBtn}
        onClick={handleMuteToggle}
        aria-label={isMuted ? 'Unmute' : 'Mute'}
        title={isMuted ? 'Unmute' : 'Mute'}
      >
        {isMuted ? (
          <i className={`bi bi-volume-mute-fill ${tvControlIcon}`} aria-hidden />
        ) : (
          <i className={`bi bi-volume-up-fill ${tvControlIcon}`} aria-hidden />
        )}
      </button>
      {layoutContext === 'desktop' && typeof onTheaterModeChange === 'function' && (
        <button
          type="button"
          className={`${tvControlBtn} ${theaterMode ? 'bg-white/20 text-white' : ''}`}
          onClick={() => {
            if (isFullscreen && !theaterMode) {
              exitFullscreen();
              onTheaterModeChange(true);
            } else {
              onTheaterModeChange(!theaterMode);
            }
          }}
          aria-label={theaterMode ? 'Exit theater mode' : 'Theater mode'}
          title={theaterMode ? 'Exit theater mode' : 'Theater mode'}
        >
          <i className={`bi bi-aspect-ratio ${tvControlIcon}`} aria-hidden />
        </button>
      )}
      <button
        type="button"
        className={tvControlBtn}
        onClick={toggleFullscreen}
        aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
      >
        <i className={`bi ${isFullscreen ? 'bi-fullscreen-exit' : 'bi-fullscreen'} ${tvControlIcon}`} aria-hidden />
      </button>
    </div>
  );

  const accordionBodyId = 'event-viewer-tv-accordion-body';

  return (
    <div className={`event-viewer show sport-${sport_id} ${theaterMode ? 'c7-tv-theater' : ''}`} style={{ minHeight: 'auto', height: 'auto' }}>
      {showNextEventBar && (
      <div className="border-b border-border">
        <button
          type="button"
          disabled={!showAccordionExpandable}
          className={`w-full flex justify-between items-center gap-2 p-2 c7-sport-gradient text-left min-h-[44px] touch-manipulation transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sport-on-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--sport-color)] ${showAccordionExpandable ? 'cursor-pointer hover:opacity-95' : 'cursor-default'}`}
          onClick={() => showAccordionExpandable && setTvAccordionExpanded((prev) => !prev)}
          aria-expanded={showAccordionExpandable ? tvAccordionExpanded : undefined}
          aria-controls={accordionBodyId}
        >
          <div className="flex items-center gap-1 text-xs font-semibold text-[var(--sport-on-color)]">
            {accordionLabelLive ? (
              <>
                <span className={`w-2 h-2 rounded-full shrink-0 ${isStartingPhase && !isLivePhase ? 'bg-amber-400 animate-pulse' : 'bg-red-500'}`} aria-hidden />
                <span className="event-result-nextevent-countdown text-[var(--sport-on-color)] font-bold text-base">
                  {isStartingPhase && !isLivePhase
                    ? (countdownData.isFootball3d && countdownData.week != null
                        ? t('STARTING_EVENT_WEEK_ID', { week: countdownData.week, event_id: safeEventIdForLabel(countdownData.eventId) })
                        : t('STARTING_EVENT_ID', { event_id: safeEventIdForLabel(countdownData.eventId) }))
                    : (countdownData.isFootball3d && countdownData.week != null
                        ? t('LIVE_EVENT_WEEK_ID', { week: countdownData.week, event_id: safeEventIdForLabel(countdownData.eventId) })
                        : t('LIVE_EVENT_ID', { event_id: safeEventIdForLabel(countdownData.eventId) }))}
                </span>
              </>
            ) : (
              <>
                <span>
                  {countdownData.isFootball3d && countdownData.week != null
                    ? t('NEXT_EVENT_WEEK_ID_STARTS_IN', { week: countdownData.week, event_id: safeEventIdForLabel(countdownData.eventId) })
                    : t('NEXT_EVENT_ID_STARTS_IN', { event_id: safeEventIdForLabel(countdownData.eventId) })}
                </span>
                <NextEventCountdown
                  eventId={countdownData.eventId}
                  eventTimestamp={countdownData.eventTimestamp}
                  endTimestamp={countdownData.endTimestamp}
                  liveReady={countdownData.isLive}
                  pulseOnStarted={countdownSecondsFromContext === 0 && !isLiveFromContext}
                  className="event-result-nextevent-countdown text-[var(--sport-on-color)] font-bold text-base"
                />
              </>
            )}
          </div>
          {showAccordionExpandable && (
            <i
              className={`bi text-[var(--sport-on-color)] text-lg transition-transform duration-300 ease-in-out shrink-0 ${tvAccordionExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}
              aria-hidden
            />
          )}
        </button>

        <div
          id={accordionBodyId}
          role="region"
          aria-label={
            accordionLabelLive
              ? (countdownData.isFootball3d && countdownData.week != null
                  ? t('LIVE_EVENT_WEEK_ID', { week: countdownData.week, event_id: safeEventIdForLabel(countdownData.eventId) })
                  : t('LIVE_EVENT_ID', { event_id: safeEventIdForLabel(countdownData.eventId) }))
              : (countdownData.isFootball3d && countdownData.week != null
                  ? t('NEXT_EVENT_WEEK_ID_STARTS_IN', { week: countdownData.week, event_id: safeEventIdForLabel(countdownData.eventId) })
                  : t('NEXT_EVENT_ID_STARTS_IN', { event_id: safeEventIdForLabel(countdownData.eventId) }))
          }
          className="overflow-hidden transition-[max-height] duration-500 ease-in-out"
          style={{ maxHeight: tvAccordionExpanded ? '2000px' : '0' }}
        >
          <div
            className={
              [
                layoutContext === 'desktop'
                  ? theaterMode
                    ? 'flex flex-col flex-1 min-h-0 gap-4'
                    : 'grid grid-cols-[1fr_minmax(280px,360px)] gap-4 min-h-0'
                  : 'min-h-0 min-w-0 w-full overflow-hidden',
                'transition-opacity duration-300 ease-in-out',
                tvAccordionExpanded ? 'opacity-100' : 'opacity-0',
              ].join(' ')
            }
            style={layoutContext === 'desktop' && !theaterMode ? { minHeight: 300 } : {}}
          >
            <div key="tv-area" className="min-h-0 min-w-0 flex-1 w-full overflow-hidden relative">
              {isActiveLayout && displayExecutionRuns
                .filter(({ scheduleId }: DisplayExecutionRun) => String(scheduleId) === String(current_schedule_id))
                .map(({ scheduleId, run: runItem }: DisplayExecutionRun) => {
                  const ri = runItem as Record<string, unknown>;
                  return (
                  <div key={String(scheduleId)} style={{ position: 'relative', zIndex: 1 }}>
                    <MiniTvCanvasWrapper
                      fullscreenRef={fullscreenTargetRef}
                      baseballBarAnchorRef={baseballBarAnchorRef}
                      className={`video-container show w-full flex items-center justify-center c7-tv-embed relative ${layoutContext === 'desktop' ? 'c7-mini-tv-desktop' : ''}`}
                      style={{ backgroundColor: '#000', minHeight: 300 }}
                      overlayChildren={
                        <div className="c7-mini-tv-video-controls">
                          {renderTvControls()}
                        </div>
                      }
                    >
                      <MiniTvViewer
                        key={`mini-tv-execution-${String(scheduleId)}-${String(ri.eventId)}-${String(ri.videoUrl)}`}
                        phase="execution"
                        event={ri.event}
                        eventResults={ri.eventResults}
                        videoUrl={ri.videoUrl}
                        videoUrls={ri.videoUrls}
                        avh={ri.avh}
                        sportId={ri.sportId}
                        onVideoReady={noopVideoReady}
                        onExecutionEnded={() => {
                          markExecutionEnded(ri?.eventId as string | number | undefined);
                          setRunEnded(scheduleId as string);
                          advanceCursorAfterExecution({
                            eventId: ri?.eventId as string | number | undefined,
                            eventTimestamp: (ri?.event as Record<string, unknown> | undefined)?.event_timestamp as number | undefined,
                            week: (ri?.event as Record<string, unknown> | undefined)?.week_id as number | undefined,
                          });
                          maybeRefetchUpcoming();
                        }}
                        showevolution={ri.showevolution as number | undefined}
                        joinElapsedSeconds={ri.joinElapsedSeconds as number}
                        initialSeekTime={ri.initialSeekTime as number | undefined}
                        initialSegmentIndex={ri.initialSegmentIndex as number | undefined}
                        initialSegmentSeekSeconds={ri.initialSegmentSeekSeconds as number | undefined}
                        otherWeekEvents={ri.otherWeekEvents}
                        weekResults={ri.weekResults}
                        muted={isMuted}
                        baseballBarAnchorRef={baseballBarAnchorRef}
                      />
                    </MiniTvCanvasWrapper>
                  </div>
                  );
                })}
              {!run && eventStarted && tvAccordionExpanded && isActiveLayout && hasValidJoinPositionData && (!joinPositionForPresentation.joiningLate || !hasVideoUrls) && (
                <MiniTvCanvasWrapper
                  key="presentation"
                  fullscreenRef={fullscreenTargetRef}
                  baseballBarAnchorRef={baseballBarAnchorRef}
                  className={`video-container show w-full flex items-center justify-center c7-tv-embed relative ${layoutContext === 'desktop' ? 'c7-mini-tv-desktop' : ''}`}
                  style={{ backgroundColor: '#000', minHeight: 300 }}
                  overlayChildren={
                    <>
                      {(shouldShowCancelledOverlay || shouldShowVideoErrorOverlay || shouldShowResultsFetchError) && (
                        <div className="absolute inset-0 z-30 flex items-center justify-center p-8">
                          <div className="max-w-5xl w-full rounded-lg border border-white/15 bg-black/80 backdrop-blur p-10 md:p-12 text-center">
                            <div className="text-3xl md:text-4xl font-bold text-white mb-5">
                              {t('TECHNICAL_ERROR') || 'Technical Error'}
                            </div>
                            <div className="text-xl md:text-2xl text-white/90 leading-relaxed">
                              {shouldShowCancelledOverlay
                                ? (t('EVENT_CANCELLED_ERROR') || 'Event cancelled due to technical error')
                                : shouldShowResultsFetchError
                                  ? (t('RESULTS_FETCH_ERROR') || 'Could not load event data. The event will advance shortly.')
                                  : (t('VIDEO_ERROR_MESSAGE') || "The event viewer encountered a technical error but the event was not cancelled. The results that follow are official and count towards your bets' settlement.")}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="c7-mini-tv-video-controls">
                        {renderTvControls()}
                      </div>
                    </>
                  }
                >
                  <MiniTvViewer
                    key={`mini-tv-presentation-${stablePresentationKeyEventId}`}
                    phase={miniTv.phase}
                    event={next_event}
                    eventResults={{
                      ...(Object.keys(effectiveEventResults != null ? effectiveEventResults : {}).length > 0 ? effectiveEventResults : {}),
                      event_id: liveEventId ?? next_event_id,
                      sport_id: sport_id,
                    }}
                    videoUrl={scheduleJustChanged ? null : miniTv.videoUrl}
                    videoUrls={scheduleJustChanged ? null : miniTv.videoUrls}
                    avh={stableAvh}
                    sportId={sport_id}
                    onVideoReady={handleMiniTvVideoReady}
                    onExecutionEnded={() => {}}
                    joinElapsedSeconds={0}
                    initialSeekTime={undefined}
                    initialSegmentIndex={undefined}
                    initialSegmentSeekSeconds={undefined}
                    showevolution={
                      (effectiveEventResults?.showevolution != null)
                        ? Number(effectiveEventResults.showevolution)
                        : 1
                    }
                    muted={isMuted}
                    baseballBarAnchorRef={baseballBarAnchorRef}
                    otherWeekEvents={footballOtherWeekEvents}
                    weekResults={footballWeekResults}
                    joiningLate={joinPositionForPresentation.joiningLate}
                  />
                </MiniTvCanvasWrapper>
              )}
            </div>

            {layoutContext === 'desktop' && (
              <div className="flex flex-col min-w-0 min-h-0" style={!theaterMode ? { maxWidth: 360 } : {}}>
                <EventBetsPanel eventId={eventIdForBetsPanel} />
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {showError && (
        <div className="p-1" style={{ overflow: 'visible' }}>
          <div className="error-container p-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-center mb-2">
                <svg className="w-6 h-6 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <h3 className="text-lg font-medium text-red-800">Event Unavailable</h3>
              </div>
              <p className="text-red-700 text-center">{safeEventIdForLabel(error_message as string)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EventViewer;
